import { useCallback } from 'react';
import { useSetRecoilState } from 'recoil';
import { vec3 } from 'gl-matrix';
import {
    doseIntensityValueAtCursorState,
    imageValueAtCursorState,
    patientSpaceCoordsState,
    secondaryImageValueAtCursorState,
} from '~/state/atoms/viewerAtoms';
import useDose from '../patient/dose/useDose';
import { Environment } from '~/engine/data/Environment';

/**
 * Updates all cursor-related Recoil states (image, secondary image, dose, patient coords)
 * for a given Environment and patient-space coordinate.
 *
 * Image and secondary image pixel values are stored as raw values; display
 * formatting (SUV conversion, unit suffixes, decimal places) is handled by
 * {@link formatValueWithUnit} at the presentation layer. Dose cursor values
 * are stored scaled-to-display (rawDoseValue * doseScale) so that
 * {@link formatDoseDisplayValue} operates on already scaled dose values.
 */
export default function useUpdateCursorRelatedStates(env: Environment | null) {
    const setPatientSpaceCoords = useSetRecoilState(patientSpaceCoordsState);
    const setDoseIntensityValueAtCursor = useSetRecoilState(
        doseIntensityValueAtCursorState
    );
    const setImageValueAtCursor = useSetRecoilState(imageValueAtCursorState);
    const setSecondaryImageValueAtCursor = useSetRecoilState(
        secondaryImageValueAtCursorState
    );
    const { dose } = useDose();
    const doseScale = dose?.doseScale ?? 1.0;

    const updateCursorRelatedStates = useCallback(
        (patientCoords: vec3): void => {
            if (!env || !patientCoords) {
                return;
            }

            setPatientSpaceCoords({ coords: patientCoords });

            const imgObject = env.image;
            const secondaryImgObject = env.secondaryImage;
            const doseObject = env.dose;

            if (imgObject) {
                const imgValue =
                    imgObject.getImageObjectValueByCoordinate(patientCoords);
                setImageValueAtCursor(imgValue);

                if (secondaryImgObject) {
                    setSecondaryImageValueAtCursor(
                        secondaryImgObject.getImageObjectValueByCoordinate(
                            patientCoords
                        )
                    );
                } else {
                    setSecondaryImageValueAtCursor(null);
                }
            }

            if (doseObject) {
                const rawDoseValue =
                    doseObject.getDoseIntensityValueAtCursorCoordinate(
                        patientCoords
                    );
                // Apply dose scale to match the scaled dose display
                const scaledDoseValue = rawDoseValue
                    ? rawDoseValue * doseScale
                    : null;
                setDoseIntensityValueAtCursor(
                    scaledDoseValue && scaledDoseValue > 0
                        ? scaledDoseValue
                        : null
                );
            }
        },
        [
            env,
            doseScale,
            setPatientSpaceCoords,
            setImageValueAtCursor,
            setSecondaryImageValueAtCursor,
            setDoseIntensityValueAtCursor,
        ]
    );

    return updateCursorRelatedStates;
}
